// severity.js — canonical severity identity for SecureShield365.
//
// Single source of truth for severity ordering, ranking, and colors. Values are
// copied verbatim from the dashboard Badge "heavy" palette in SecureShield365.js
// so the PDF (ExecutiveReport.js) and Excel (FindingsExport.js) exports match the
// dashboard's severity identity exactly.
//
// Framework-agnostic, plain JS — no @react-pdf/renderer or xlsx imports — so both
// export modules can consume it without pulling in each other's dependencies.

export const SEVERITY_ORDER = ["critical", "high", "medium", "low", "informational"];

export const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };

export const SEVERITY_PALETTE = {
  critical:      { bg: "#44256D", text: "#E9D5FF", border: "#8B5CF6", label: "Critical" },
  high:          { bg: "#621520", text: "#FECACA", border: "#EF4444", label: "High" },
  medium:        { bg: "#7A3209", text: "#FFB66D", border: "#F97316", label: "Medium" },
  low:           { bg: "#4F4715", text: "#FEF08A", border: "#FACC15", label: "Low" },
  informational: { bg: "#243047", text: "#94A3B8", border: "#475569", label: "Informational" },
};

export const sevKey = (s) => (s || "").toString().toLowerCase();

// Canonical findings comparator — single source of truth for finding order
// across the PDF "Top Priority Findings" list and the Excel "Action Items"
// sheet. Severity rank dominates (Critical first); ties break on score_weight
// descending (heavier-weighted setting first). Unknown severity → 99 (sorts
// last); missing score_weight → 0.
export const compareFindings = (a, b) => {
  const ra = SEVERITY_RANK[sevKey(a.severity)] ?? 99;
  const rb = SEVERITY_RANK[sevKey(b.severity)] ?? 99;
  if (ra !== rb) return ra - rb;
  return (b.score_weight || 0) - (a.score_weight || 0);
};

// Unknown/missing severity falls back to informational (neutral slate),
// NOT low — so an unrecognized value never renders as a blue "Low".
export const sevPalette = (s) => SEVERITY_PALETTE[sevKey(s)] || SEVERITY_PALETTE.informational;
